import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Badge,
} from "react-bootstrap";
import {
  History,
  Search,
  RefreshCcw,
  ArrowUpCircle,
  ArrowDownCircle,
  Info,
  ExternalLink,
} from "lucide-react";
import axios from "axios";

// Layout Components
import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";
import { useAuth } from "./Home1";

const API_URL = "https://beaconsms.com/oldsite/api/all_transactions_api.php";

const AllTransactions = () => {
  const { user, isLoading: authLoading } = useAuth();
  const isAdmin = user?.id === "71";

  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?adminId=71`);
      setTransactions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    document.title = "Global Transaction Logs - beaconSMS";
    fetchTransactions();
  }, [fetchTransactions]);

  const filteredTransactions = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return transactions.filter(
      (t) =>
        (t.username || "").toLowerCase().includes(term) ||
        (t.tx_memo || "").toLowerCase().includes(term) ||
        (t.tx_rand_id || "").toLowerCase().includes(term) ||
        (t.tx_gateway || "").toLowerCase().includes(term),
    );
  }, [transactions, searchTerm]);

  if (authLoading)
    return (
      <div className='text-center py-5'>
        <Spinner animation='border' variant='primary' />
      </div>
    );

  if (!isAdmin)
    return (
      <Container className='mt-5 text-center'>
        <h2>Access Denied</h2>
      </Container>
    );

  return (
    <Container>
      <Header user={user} />
      <Row className='mt-3 px-md-3'>
        <Col md='3'>
          <Logout Menu='Login Menu' user={user?.username} />
          <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
          {isAdmin && <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />}
        </Col>

        <Col md='9'>
          <div className='bg-white p-4 border rounded shadow-sm'>
            <div className='d-flex justify-content-between align-items-center mb-4 border-bottom pb-3'>
              <div className='d-flex align-items-center text-primary'>
                <History className='me-2' />
                <h4 className='mb-0 fw-bold'>Global Transaction History</h4>
              </div>
              <Button
                variant='outline-primary'
                size='sm'
                onClick={fetchTransactions}
              >
                <RefreshCcw size={16} className={loading ? "fa-spin" : ""} />
              </Button>
            </div>

            <InputGroup className='mb-4 shadow-sm'>
              <InputGroup.Text className='bg-white border-end-0'>
                <Search size={18} className='text-muted' />
              </InputGroup.Text>
              <Form.Control
                className='border-start-0 ps-0'
                placeholder='Search by user, reference, gateway or description...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <div className='table-responsive'>
              <Table hover className='align-middle border small'>
                <thead
                  className='bg-light text-uppercase'
                  style={{ fontSize: "11px" }}
                >
                  <tr>
                    <th>User</th>
                    <th>Type/Memo</th>
                    <th>Amount</th>
                    <th>Balance Info</th>
                    <th>Status/Date</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: "13px" }}>
                  {loading ? (
                    <tr>
                      <td colSpan='5' className='text-center py-5'>
                        <Spinner animation='border' variant='primary' />
                      </td>
                    </tr>
                  ) : filteredTransactions.length > 0 ? (
                    filteredTransactions.map((t, i) => {
                      const isCredit =
                        t.tx_gateway === "Admin-Manual" ||
                        t.tx_memo.toLowerCase().includes("credit");
                      return (
                        <tr key={i}>
                          <td className='fw-bold'>@{t.username}</td>
                          <td>
                            <div className='fw-medium'>
                              <Badge
                                bg={
                                  t.tx_gateway === "Admin-Manual"
                                    ? "dark"
                                    : "info"
                                }
                                className='me-1'
                              >
                                {t.tx_gateway}
                              </Badge>
                            </div>
                            <div
                              className='text-muted small mt-1'
                              style={{ maxWidth: "200px" }}
                            >
                              {t.tx_memo}
                            </div>
                            <div
                              className='text-xs text-muted'
                              style={{ fontSize: "10px" }}
                            >
                              REF: {t.tx_rand_id}
                            </div>
                          </td>
                          <td
                            className={
                              isCredit
                                ? "text-success fw-bold"
                                : "text-danger fw-bold"
                            }
                          >
                            {isCredit ? (
                              <ArrowUpCircle size={12} className='me-1' />
                            ) : (
                              <ArrowDownCircle size={12} className='me-1' />
                            )}
                            {t.tx_amount}
                          </td>
                          <td>
                            <div
                              className='text-muted'
                              style={{ fontSize: "11px" }}
                            >
                              Before: {Number(t.tx_balance_before).toFixed(2)}
                            </div>
                            <div className='fw-bold'>
                              After: {Number(t.tx_balance_after).toFixed(2)}
                            </div>
                          </td>
                          <td>
                            <Badge
                              bg={
                                t.tx_status === "Success"
                                  ? "success-subtle"
                                  : "secondary-subtle"
                              }
                              className={
                                t.tx_status === "Success"
                                  ? "text-success border border-success-subtle"
                                  : "text-muted border"
                              }
                            >
                              {t.tx_status}
                            </Badge>
                            <div
                              className='text-muted x-small mt-1'
                              style={{ fontSize: "10px" }}
                            >
                              {t.tx_created}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan='5' className='text-center py-4'>
                        No transactions found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default AllTransactions;
